<script lang="ts">
import type { PopupField, SystemSettingsData } from '@/views/Settings/SystemSettings.vue';
import type { EmitsToListeners } from '@/types/core';

type DataParams = Record<string, any>;

export interface Props {
  popupData?: DataParams,
  popupTitle?: string,
  popupInfo?: string,
  popupFields?: PopupField[],
  popupAction: (...params: any[]) => Promise<any>;
}

export interface  Emits {
  'close': [],
  'success': [],
}

export interface SystemSettingsParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
</script>

<script setup lang="ts">
//#region import
import { ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useError } from '@/composables/useError';
import { isErrorResponse, typeGuard } from '@/types/core';
//#endregion

//#region props
const props = withDefaults(defineProps<Props>(), {
  popupData: () => ({}),
  popupTitle: '',
  popupInfo: '',
  popupFields: () => ([]),
});
//#endregion

//#region emits
const emit = defineEmits<Emits>();
//#endregion

//#region const
const errors = ref<any>({});
const { getError } = useError({ errors });
//#endregion

//#region data
const isVisible = ref(false);
const isLoading = ref(false);

const data = ref<DataParams>({});
//#endregion

//#region methods
const open = () => {
  data.value = cloneDeep(props.popupData);
  isVisible.value = true;
};

const handleFieldInput = (field: PopupField, value: string) => {
  data.value[field.key] = field.normalizeValue?.(value) ?? value;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};

const preventSymbol = (type: string, e: KeyboardEvent) => {
  if(type === 'number' || type === 'input-select') {
    if (e.preventDefault && !(/^[0-9]*\.?[0-9]*$/).test(e.key) && !['Delete', 'Backspace'].includes(e.code)) {
      if(e.preventDefault) e.preventDefault();
    }
  }
};

const handleSave = async () => {
  try {
    await props.popupAction(data.value);
    emit('success');
    handleClose();
  } catch (error: unknown) {
    if (isErrorResponse(error)) {
      errors.value = error.data?.errors || {};
    }
  }
};

const addPromoCodeLevel = () => {
  if(typeGuard<Pick<SystemSettingsData, 'max_promo_codes_levels'>>(data.value)) {
    const current = data.value.max_promo_codes_levels || [];
    const last = current[current.length - 1] || { ftd_count: 0, max_amount: 0 };
    const nextFtd = Number(last.ftd_count) || 0;

    data.value.max_promo_codes_levels = [
      ...current,
      {
        ftd_count: current.length ? nextFtd + 1 : 0,
        max_amount: 0,
      },
    ];
  }
};

const removePromoCodeLevel = (index: number) => {
  if (index === 0 && data.value.max_promo_codes_levels.length > 1) {
    return;
  }
  if(typeGuard<Pick<SystemSettingsData, 'max_promo_codes_levels'>>(data.value)) {
    data.value.max_promo_codes_levels = (data.value.max_promo_codes_levels || []).filter((_, i) => i !== index);
  }
};
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="system-settings-popup"
    :title="$t(popupTitle)"
    :width="500"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div
          v-if="popupInfo"
          class="system-settings-popup__info"
        >
          {{ $t(popupInfo) }}
        </div>
        <template
          v-for="(field, i) in popupFields"
          :key="i"
        >
          <CardTitle
            v-if="field.type === 'boolean'"
            :title="$t(field.title)"
          >
            <UiSwitch
              v-model="data[field.key]"
              :status-text="$t(field.label ? field.label(data[field.key]) : '')"
              :status-type="data[field.key] ? 'positive' : 'negative'"
            />
          </CardTitle>

          <UiInput
            v-if="field.type === 'text' || field.type === 'password-text' || field.type === 'number'"
            :model-value="data[field.key]"
            :label="$t(field.title)"
            autosize
            size="big"
            :placeholder="$t(field.title)"
            :type="field.type"
            :hint="field.hint ? $t(field.hint) : ''"
            :hint-is-html="!!field.hintIsHtml"
            :post-text="field.renderPost && field.renderPost()"
            :error="errors[field.key]"
            :disabled="field.disabled && field.disabled(data)"
            @update:model-value="handleFieldInput(field, $event)"
            @change="preventSymbol(field.type, $event)"
          />

          <UiSelectElement
            v-if="field.type === 'select' || field.type === 'select-grouped' || field.type === 'select-element'"
            v-model="data[field.key]"
            :placeholder="$t(field.placeholder || field.title || 'crm.segments.filters.select_rule_placeholder')"
            :label="$t(field.title)"
            :list="field.options!()"
            :filterable="field.type !== 'select'"
            size="big"
            option-value-param="code"
            option-label-param="name"
            :disabled="field.disabled && field.disabled(data)"
            :error="errors[field.key]"
          >
            <template
              v-if="field.type === 'select-grouped'"
              v-slot:options
            >
              <ElOptionGroup
                v-for="item in field.options!()"
                :key="item.category_id"
                :label="item.category_name"
              >
                <ElOption
                  v-for="group in item[field.groupKey!]"
                  :key="group.id"
                  :label="group.name"
                  :value="group.id"
                  :disabled="group.disabled"
                />
              </ElOptionGroup>
            </template>
          </UiSelectElement>

          <UiSelectElement
            v-if="field.type === 'select-multiple'"
            v-model="data[field.key]"
            :placeholder="$t(field.placeholder || field.title)"
            :label="$t(field.title)"
            :list="field.options!()"
            filterable
            multiple
            search-param="name"
            :max-collapse-tags="5"
            collapse-tag-tooltip
            option-name-param="name"
            option-label-param="code"
            option-value-param="code"
            :disabled="field.disabled && field.disabled(data)"
            :error="errors[field.key]"
            size="big"
          />

          <div
            v-if="field.type === 'input-select'"
            class="t:space-y-8"
          >
            <UiLabel
              v-if="field.title"
              :label="field.title ? $t(field.title) : ''"
            />
            <div class="t:grid t:grid-cols-2 t:gap-8">
              <UiInput
                v-model="data[field.key]"
                autosize
                size="big"
                :placeholder="$t(field.title)"
                type="text"
                :error="getError(field.errorPathInput || field.key)"
                :disabled="field.disabled && field.disabled(data)"
                @change="preventSymbol(field.type, $event)"
              />
              <UiSelectElement
                v-model="data[field.selectKey!]"
                :placeholder="$t(field.placeholder || '')"
                :list="field.options!()"
                size="big"
                filterable
                option-value-param="code"
                :error="getError(field.errorPathSelect || field.selectKey || field.key)"
                :disabled="field.disabled && field.disabled(data)"
              />
            </div>
          </div>

          <UiRadioGroup
            v-if="field.type === 'radio'"
            v-model="data[field.key]"
            :label="$t(field.title)"
            :hint="field.hint && $t(field.hint)"
            :options="field.options!()"
            :disabled="field.disabled && field.disabled(data)"
            :class="[{'system-settings-popup__item_compact': field.compact}]"
            direction="col"
          />

          <UiTextarea
            v-if="field.type === 'textarea'"
            v-model="data[field.key]"
            :label="$t(field.title)"
            :disabled="field.disabled && field.disabled(data)"
            :placeholder="$t(field.placeholder || field.title)"
            :rows="3"
            :error="getError([...field.path || [], field.key])"
          />

          <div
            v-if="field.type === 'promo-codes-levels'"
            class="system-settings-popup__promo-codes-levels t:space-y-16"
          >
            <div class="t:flex t:justify-between">
              <UiLabel
                :label="$t(field.title)"
                bold
              />
              <UiButton
                icon="plus"
                no-border
                @click="addPromoCodeLevel"
              >
                {{ $t('affiliates.offer_popup.add_level') }}
              </UiButton>
            </div>
            <div
              v-if="data.max_promo_codes_levels?.length"
              class="t:space-y-8"
            >
              <div
                v-for="(item, index) in data.max_promo_codes_levels"
                :key="index"
                class="t:flex t:items-center t:gap-8"
              >
                <UiInput
                  v-model="item.ftd_count"
                  pre-icon="greater-than-equal"
                  class="t:flex-[1_1_0]"
                  post-text="FTD"
                  :disabled="index === 0"
                  error-tooltip
                  :error="getError(['max_promo_codes_levels', index, 'ftd_count'])"
                  @change="preventSymbol"
                />
                <UiIcon
                  name="dash"
                  color="var(--text-body-tertiary)"
                  :size="14"
                  substyle="fa-solid"
                />
                <UiInput
                  v-model.number="item.max_amount"
                  class="t:flex-[1_1_0]"
                  error-tooltip
                  :error="getError(['max_promo_codes_levels', index, 'max_amount'])"
                  @change="preventSymbol"
                />
                <UiIcon
                  size="14px"
                  class="t:cursor-pointer"
                  name="times"
                  color="var(--text-negative)"
                  :disabled="index === 0 && data.max_promo_codes_levels.length > 1"
                  @click="removePromoCodeLevel(+index)"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.system-settings-popup {
  &__info {
    color: var(--text-body);
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }
  &__item{
    &_compact {
      margin-bottom: 8px;
    }
  }
  &__promo-codes-levels {
    padding: 10px;
    border-radius: 8px;
    background: var(--background-surface);
    box-sizing: border-box;
    position: relative;
  }
}
</style>
